import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { ShoppingCart, Menu as MenuIcon, X, Search } from "lucide-react";
import BrandLogo from "@/components/BrandLogo";
import LanguageSelector from "@/components/LanguageSelector";
import AccountMenu from "@/components/AccountMenu";
import { useApp } from "@/context/AppContext";

export default function Header() {
  const { t, cartCount } = useApp();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const navigate = useNavigate();

  const nav = [
    { to: "/", label: t("nav.home"), testid: "nav-home" },
    { to: "/menu", label: t("nav.menu"), testid: "nav-menu" },
    { to: "/packages", label: t("nav.packages"), testid: "nav-packages" },
    { to: "/build", label: t("nav.build"), testid: "nav-build" },
    { to: "/quote", label: t("nav.quote"), testid: "nav-quote" },
    { to: "/contact", label: t("nav.contact"), testid: "nav-contact" },
  ];

  const submitSearch = (e) => {
    e.preventDefault();
    const q = search.trim();
    if (!q) return;
    navigate(`/menu?q=${encodeURIComponent(q)}`);
    setOpen(false);
  };

  return (
    <header className="sticky top-0 z-40 border-b border-border/60 bg-background/85 backdrop-blur-xl">
      <div className="container-x flex h-20 items-center justify-between gap-4 sm:h-24 md:h-28">
        <Link to="/" data-testid="header-brand-link" className="flex items-center gap-3">
          <BrandLogo />
        </Link>

        <nav className="hidden items-center gap-1 lg:flex">
          {nav.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === "/"}
              data-testid={n.testid}
              className={({ isActive }) =>
                `whitespace-nowrap rounded-full px-3 py-2 text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-secondary text-secondary-foreground"
                    : "text-secondary/80 hover:bg-muted"
                }`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <form
            onSubmit={submitSearch}
            className="hidden items-center gap-2 rounded-full border border-border/70 bg-muted/60 px-3 py-1.5 md:flex"
          >
            <Search className="h-4 w-4 text-muted-foreground" aria-hidden />
            <input
              data-testid="header-search-input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t("search.placeholder")}
              className="w-56 bg-transparent text-sm placeholder:text-muted-foreground focus:outline-none"
            />
          </form>

          <LanguageSelector testid="language-selector-desktop" />

          <AccountMenu />

          <Link
            to="/cart"
            data-testid="header-cart-link"
            className="relative inline-flex h-10 w-10 items-center justify-center rounded-full border border-border/70 bg-background hover:bg-muted"
            aria-label="Cart"
          >
            <ShoppingCart className="h-4 w-4" aria-hidden />
            {cartCount > 0 && (
              <span
                data-testid="cart-badge-count"
                className="absolute -right-1 -top-1 grid h-5 min-w-5 place-items-center rounded-full bg-primary px-1 text-[10px] font-semibold text-primary-foreground"
              >
                {cartCount}
              </span>
            )}
          </Link>

          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-border/70 bg-background lg:hidden"
            aria-label="Menu"
          >
            {open ? <X className="h-4 w-4" /> : <MenuIcon className="h-4 w-4" />}
          </button>
        </div>
      </div>

      {open && (
        <div
          data-testid="mobile-nav-panel"
          className="border-t border-border/60 bg-background lg:hidden"
        >
          <div className="container-x flex flex-col gap-2 py-4">
            <form
              onSubmit={submitSearch}
              className="flex items-center gap-2 rounded-full border border-border/70 bg-muted/60 px-3 py-2"
            >
              <Search className="h-4 w-4 text-muted-foreground" />
              <input
                data-testid="mobile-search-input"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t("search.placeholder")}
                className="flex-1 bg-transparent text-sm focus:outline-none"
              />
            </form>
            {nav.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/"}
                data-testid={`${n.testid}-mobile`}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `rounded-full px-4 py-2 text-sm font-medium ${
                    isActive
                      ? "bg-secondary text-secondary-foreground"
                      : "text-secondary/80 hover:bg-muted"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
            <div className="pt-1">
              <LanguageSelector testid="language-selector-mobile" />
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
